// Generated from Swagger on 2025-08-27T14:46:24.215Z
import {
  useSuspenseQuery,
  useQuery,
  useMutation,
  useQueryClient,
  UseSuspenseQueryOptions,
  UseQueryOptions,
  UseMutationOptions,
} from "@tanstack/react-query";
import { ApiRequestOptions, useApiClient } from "../client";
import { creationEntitiesKeys } from "../creation-entities-keys";
import {
  UserInfoResponseDto,
  UpdateMailRequestDto,
  UserProfileResponseDto,
  ResetPasswordRequestDto,
  CompleteProfileRequestDto,
} from "../interfaces/user-profile.interface";

// Query keys for user-profile-related queries
export const userProfileKeys = {
  all: ["user-profile"] as const,
  userinfo: () => [...userProfileKeys.all, "userinfo"] as const,
  email_mutation: () => [...userProfileKeys.all, "email_mutation"] as const,
  profile: () => [...userProfileKeys.all, "profile"] as const,
  reset_password_mutation: () =>
    [...userProfileKeys.all, "reset_password_mutation"] as const,
  reset_password_by_mail_mutation: () =>
    [...userProfileKeys.all, "reset_password_by_mail_mutation"] as const,
  resend_email_verification_mutation: () =>
    [...userProfileKeys.all, "resend_email_verification_mutation"] as const,
  complete_profile_mutation: () =>
    [...userProfileKeys.all, "complete_profile_mutation"] as const,
};

/**
 * Get user and permission Informations
 */
export function useUserProfileuserinfo(
  options?: Omit<
    UseSuspenseQueryOptions<UserInfoResponseDto>,
    "queryKey" | "queryFn"
  >,
) {
  const { apiRequest } = useApiClient();

  return useSuspenseQuery({
    queryKey: [...userProfileKeys.userinfo(), ...creationEntitiesKeys.list()],
    queryFn: () => apiRequest<UserInfoResponseDto>(`/user-profile/userinfo`),
    // Default cache-disabling options (can be overridden via options)
    staleTime: 0,
    gcTime: 0, // replaces cacheTime in v5
    refetchOnMount: "always",
    ...options,
  });
}

/**
 * Non-suspense version of useUserProfileuserinfo
 */
export function useUserProfileuserinfoQuery(
  options?: Omit<UseQueryOptions<UserInfoResponseDto>, "queryKey" | "queryFn">,
) {
  const { apiRequest } = useApiClient();

  return useQuery({
    queryKey: [...userProfileKeys.userinfo(), ...creationEntitiesKeys.list()],
    queryFn: () => apiRequest<UserInfoResponseDto>(`/user-profile/userinfo`),
    // Default cache-disabling options (can be overridden via options)
    staleTime: 0,
    gcTime: 0, // replaces cacheTime in v5
    refetchOnMount: "always",
    ...options,
  });
}

/**
 * Fetch User Info Manually
 */
export function useFetchUserInfo() {
  const { apiRequest } = useApiClient();

  const fetchUserInfo = async () => {
    return apiRequest<UserInfoResponseDto>("/user-profile/userinfo", "GET", undefined, undefined, {
      skipGlobalErrorHandler: true,
      throwOnError: true,
    });
  };

  return { fetchUserInfo };
}

/**
 * Update email
 */
export function useUserProfileUpdateEmail(
  options?: Omit<
    UseMutationOptions<any, Error, UpdateMailRequestDto>,
    "mutationFn"
  >,
) {
  const { apiRequest } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: (data: UpdateMailRequestDto) =>
      apiRequest<any>(`/user-profile/email`, "PATCH", data),
    onSuccess: (data, variables, context) => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: userProfileKeys.all });
      // Invalidate all queries that contain creation entities keys
      queryClient.invalidateQueries({
        predicate: (query) => {
          const queryKey = query.queryKey as string[];
          return queryKey.includes("creation-entities");
        },
      });
      // Call user-provided onSuccess if it exists
      options?.onSuccess?.(data, variables, context);
    },
  });
}

/**
 * Complete user profile
 */
export function useUserProfileCompleteProfile(
  options?: Omit<
    UseMutationOptions<any, Error, CompleteProfileRequestDto>,
    "mutationFn"
  >,
  requestOptions?: ApiRequestOptions,
) {
  const { apiRequest } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: (data: CompleteProfileRequestDto) =>
      apiRequest<any>(`/user-profile/complete-profile`, "PATCH", data, undefined, requestOptions),
    onSuccess: (data, variables, context) => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: userProfileKeys.all });
      // Invalidate all queries that contain creation entities keys
      queryClient.invalidateQueries({
        predicate: (query) => {
          const queryKey = query.queryKey as string[];
          return queryKey.includes("creation-entities");
        },
      });
      // Call user-provided onSuccess if it exists
      options?.onSuccess?.(data, variables, context);
    },
  });
}

/**
 * Get own user details
 */
export function useUserProfileFind(
  options?: Omit<
    UseSuspenseQueryOptions<UserProfileResponseDto>,
    "queryKey" | "queryFn"
  >,
) {
  const { apiRequest } = useApiClient();

  return useSuspenseQuery({
    queryKey: [...userProfileKeys.profile(), ...creationEntitiesKeys.list()],
    queryFn: () => apiRequest<UserProfileResponseDto>(`/user-profile`),
    // Default cache-disabling options (can be overridden via options)
    staleTime: 0,
    gcTime: 0, // replaces cacheTime in v5
    refetchOnMount: "always",
    ...options,
  });
}

/**
 * Non-suspense version of useUserProfileFind
 */
export function useUserProfileFindQuery(
  options?: Omit<
    UseQueryOptions<UserProfileResponseDto>,
    "queryKey" | "queryFn"
  >,
) {
  const { apiRequest } = useApiClient();

  return useQuery({
    queryKey: [...userProfileKeys.profile(), ...creationEntitiesKeys.list()],
    queryFn: () => apiRequest<UserProfileResponseDto>(`/user-profile`),
    // Default cache-disabling options (can be overridden via options)
    staleTime: 0,
    gcTime: 0, // replaces cacheTime in v5
    refetchOnMount: "always",
    ...options,
  });
}

/**
 * Send password reset mail
 */
export function useUserProfileresetPassword(
  options?: Omit<UseMutationOptions<any, Error, void>, "mutationFn">,
) {
  const { apiRequest } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: () => apiRequest<any>(`/user-profile/reset-password`, "POST"),
    onSuccess: (data, variables, context) => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: userProfileKeys.all });
      // Invalidate all queries that contain creation entities keys
      queryClient.invalidateQueries({
        predicate: (query) => {
          const queryKey = query.queryKey as string[];
          return queryKey.includes("creation-entities");
        },
      });
      // Call user-provided onSuccess if it exists
      options?.onSuccess?.(data, variables, context);
    },
  });
}

/**
 * Send password reset mail without logged in user
 */
export function useUserProfileresetPasswordByMail(
  options?: Omit<
    UseMutationOptions<any, Error, ResetPasswordRequestDto>,
    "mutationFn"
  >,
) {
  const { apiRequest } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: (data: ResetPasswordRequestDto) =>
      apiRequest<any>(`/user-profile/reset-password-by-mail`, "POST", data),
    onSuccess: (data, variables, context) => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: userProfileKeys.all });
      // Invalidate all queries that contain creation entities keys
      queryClient.invalidateQueries({
        predicate: (query) => {
          const queryKey = query.queryKey as string[];
          return queryKey.includes("creation-entities");
        },
      });
      // Call user-provided onSuccess if it exists
      options?.onSuccess?.(data, variables, context);
    },
  });
}

/**
 * Resend email verification
 */
export function useUserProfileresendEmailVerification(
  options?: Omit<UseMutationOptions<any, Error, void>, "mutationFn">,
) {
  const { apiRequest } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: () =>
      apiRequest<any>(`/user-profile/resend-email-verification`, "POST"),
    onSuccess: (data, variables, context) => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: userProfileKeys.all });
      // Invalidate all queries that contain creation entities keys
      queryClient.invalidateQueries({
        predicate: (query) => {
          const queryKey = query.queryKey as string[];
          return queryKey.includes("creation-entities");
        },
      });
      // Call user-provided onSuccess if it exists
      options?.onSuccess?.(data, variables, context);
    },
  });
}
